/**
 * GmapApiLogger — lightweight singleton that tracks every Google Maps API call
 * made from the transport live-tracking pages and prints styled logs to the
 * browser console.
 *
 * Usage:
 *   GmapApiLogger.log('Directions', 'CALL', { vehicle: 5, waypoints: 12 });
 *   GmapApiLogger.log('Directions', 'CACHE_HIT', { vehicle: 5 });
 *   GmapApiLogger.summary();   // print totals table
 *
 * Run  GmapApiLogger.summary()  in DevTools console at any time to see totals.
 */

export type GmapApiType = 'Maps JS Load' | 'Directions' | 'Geocoding';
export type GmapApiEvent = 'CALL' | 'CACHE_HIT' | 'SUCCESS' | 'ERROR' | 'SKIP';

interface LogEntry {
  time: string;
  api: GmapApiType;
  event: GmapApiEvent;
  source: string;
  detail: Record<string, unknown>;
}

interface Counter {
  CALL: number;
  CACHE_HIT: number;
  SUCCESS: number;
  ERROR: number;
  SKIP: number;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES: Record<GmapApiEvent, string> = {
  CALL:      'background:#1565C0;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
  CACHE_HIT: 'background:#2E7D32;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
  SUCCESS:   'background:#00695C;color:#fff;padding:2px 6px;border-radius:3px',
  ERROR:     'background:#B71C1C;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
  SKIP:      'background:#616161;color:#fff;padding:2px 6px;border-radius:3px',
};

const API_COLORS: Record<GmapApiType, string> = {
  'Maps JS Load': '#F57F17',
  'Directions':   '#1565C0',
  'Geocoding':    '#6A1B9A',
};

class GmapApiLoggerClass {
  private history: LogEntry[] = [];
  private counters = new Map<GmapApiType, Counter>();

  private getCounter(api: GmapApiType): Counter {
    if (!this.counters.has(api)) {
      this.counters.set(api, { CALL: 0, CACHE_HIT: 0, SUCCESS: 0, ERROR: 0, SKIP: 0 });
    }
    return this.counters.get(api)!;
  }

  log(
    api: GmapApiType,
    event: GmapApiEvent,
    source: string,
    detail: Record<string, unknown> = {},
  ): void {
    const now = new Date();
    const time = now.toTimeString().slice(0, 8);

    this.getCounter(api)[event]++;
    const totalCalls = this.getCounter(api).CALL;

    const entry: LogEntry = { time, api, event, source, detail };
    this.history.push(entry);

    // ── console output ──────────────────────────────────────────────────────
    const apiColor = API_COLORS[api];
    const detailStr = Object.keys(detail).length
      ? ' | ' + Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(' · ')
      : '';

    if (event === 'CALL') {
      console.log(
        `%c ${event} %c ${api} %c [#${totalCalls}] ${source}${detailStr} @ ${time}`,
        STYLES[event],
        `color:${apiColor};font-weight:bold`,
        'color:#555',
      );
    } else if (event === 'CACHE_HIT') {
      console.log(
        `%c ${event} %c ${api} %c ${source}${detailStr} @ ${time} (saved 1 API call)`,
        STYLES[event],
        `color:${apiColor};font-weight:bold`,
        'color:#555',
      );
    } else if (event === 'ERROR') {
      console.error(
        `%c ${event} %c ${api} %c ${source}${detailStr} @ ${time}`,
        STYLES[event],
        `color:${apiColor};font-weight:bold`,
        'color:#555',
      );
    } else {
      console.log(
        `%c ${event} %c ${api} %c ${source}${detailStr} @ ${time}`,
        STYLES[event],
        `color:${apiColor};font-weight:bold`,
        'color:#555',
      );
    }
  }

  /** Print a summary table of all API calls so far. Call from DevTools: GmapApiLogger.summary() */
  summary(): void {
    console.group('%c📊 Google Maps API Call Summary', 'font-size:14px;font-weight:bold;color:#1565C0');

    const rows: Record<string, unknown>[] = [];
    this.counters.forEach((c, api) => {
      rows.push({
        API: api,
        'Actual Calls': c.CALL,
        'Cache Hits (saved)': c.CACHE_HIT,
        'Success': c.SUCCESS,
        'Errors': c.ERROR,
        'Skipped': c.SKIP,
        'Est. Cost (USD)': this.estimateCost(api, c.CALL),
      });
    });

    if (rows.length > 0) {
      console.table(rows);
    } else {
      console.log('No API calls recorded yet.');
    }

    const totalCalls = [...this.counters.values()].reduce((s, c) => s + c.CALL, 0);
    const totalSaved = [...this.counters.values()].reduce((s, c) => s + c.CACHE_HIT, 0);
    console.log(`%cTotal API calls: ${totalCalls}  |  Total cache hits (money saved): ${totalSaved}`, 'font-weight:bold');
    console.groupEnd();
  }

  /** Print all raw log entries */
  history_log(): void {
    console.table(this.history.map(e => ({
      Time: e.time,
      API: e.api,
      Event: e.event,
      Source: e.source,
      Detail: JSON.stringify(e.detail),
    })));
  }

  /** Reset all counters and history */
  reset(): void {
    this.history = [];
    this.counters.clear();
    console.log('%c[GmapApiLogger] Counters reset.', 'color:#888');
  }

  private estimateCost(api: GmapApiType, calls: number): string {
    const FREE_CAP: Record<GmapApiType, number> = {
      'Maps JS Load': 10000,
      'Directions':   10000,
      'Geocoding':    10000,
    };
    const RATE_PER_1000: Record<GmapApiType, number> = {
      'Maps JS Load': 7.00,
      'Directions':   5.00,
      'Geocoding':    5.00,
    };
    const over = Math.max(0, calls - FREE_CAP[api]);
    return over === 0 ? '$0.00 (within free cap)' : `$${((over / 1000) * RATE_PER_1000[api]).toFixed(4)}`;
  }
}

// Singleton — attach to window so DevTools can call GmapApiLogger.summary()
const GmapApiLogger = new GmapApiLoggerClass();
(window as any)['GmapApiLogger'] = GmapApiLogger;

export { GmapApiLogger };
